import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { listMyClubs, type ClubRow } from "../lib/api";

/** The club the caller is managing on /manage/:clubId/... pages (name and
 *  their role), or an error if they can't manage it. */
export function useManagedClub(clubId: string): { club: ClubRow | null; error: string | null } {
  const [club, setClub] = useState<ClubRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setClub(null);
    setError(null);
    listMyClubs()
      .then((res) => {
        const found = res.clubs.find((c) => c.clubId === clubId);
        if (found) setClub(found);
        else setError("You don't manage this club.");
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load club."));
  }, [clubId]);
  return { club, error };
}

/** Breadcrumb + links between a club's Manage sections. */
export function ManageClubNav({ club }: { club: Pick<ClubRow, "clubId" | "name"> }) {
  const base = `/manage/${encodeURIComponent(club.clubId)}`;
  const linkClass = ({ isActive }: { isActive: boolean }) => (isActive ? "active" : "");
  return (
    <>
      <p className="muted" style={{ fontSize: 13 }}>
        <Link to="/manage">Manage</Link> / <Link to={base}>{club.name}</Link>
      </p>
      <div className="manage-subnav" role="navigation" aria-label={`Manage ${club.name}`}>
        <NavLink to={base} end className={linkClass}>Events</NavLink>
        <NavLink to={`${base}/tracks`} className={linkClass}>Tracks</NavLink>
        <NavLink to={`${base}/news`} className={linkClass}>News</NavLink>
        <NavLink to={`${base}/hobby-shops`} className={linkClass}>Hobby Shops</NavLink>
        <NavLink to={`${base}/stream`} className={linkClass}>Streaming</NavLink>
        <NavLink to={`${base}/advanced`} className={linkClass}>Advanced settings</NavLink>
      </div>
    </>
  );
}

/** Moves one id up (-1) or down (+1) in a list; no-op at either end. */
export function moveId(ids: string[], id: string, delta: -1 | 1): string[] {
  const i = ids.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const next = [...ids];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}
